import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useNavigate, useParams } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Title,
  Text,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Box,
  Paper,
  Image,
  FileButton,
  Flex,
  Loader,
  Alert,
  ActionIcon,
  Progress,
  Badge,
  Switch,
} from "@mantine/core";
import {
  IconUpload,
  IconTrash,
  IconGripVertical,
  IconAlertTriangle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconBook,
  IconTrophy,
  IconSchool,
  IconUsers,
  IconCalendarEvent,
  IconFileText,
} from "@tabler/icons-react";
import VideoModal from "./VideoModal";
import DeleteCourseModal from "./DeleteCourseModal";
import DeleteVideoModal from "./DeleteVideoModal";
import { handleGenericError } from "../../utils/errorHandler";
import PageContent from "../shared/PageContent/PageContent";
import "../Organization/OrganizationPage.css";
import "./ProgramDetailsPage.css";
import "./CourseFormPage.css";

const ADMIN_SECTIONS = [
  {
    id: "courses",
    label: "Training Courses",
    icon: IconBook,
    path: "/admin?tab=courses",
  },
  {
    id: "course-creation",
    label: "Course Creation",
    icon: IconPlus,
    path: null,
  },
  {
    id: "incentives",
    label: "Incentives",
    icon: IconTrophy,
    path: "/admin?tab=incentives",
  },
  { id: "programs", label: "Programs", icon: IconSchool, path: "/programs" },
  {
    id: "groups",
    label: "Custom Groups",
    icon: IconUsers,
    path: "/admin?tab=groups",
  },
  {
    id: "events",
    label: "Events",
    icon: IconCalendarEvent,
    path: "/reps/downline?tab=events",
  },
  {
    id: "documents",
    label: "Documents",
    icon: IconFileText,
    path: "/admin?tab=documents",
  },
];

// Sortable Video Item Component
function SortableVideoItem({
  video,
  isUploading,
  uploadProgress,
  onVideoClick,
  onDeleteClick,
  onRetryClick,
  getVideoThumbnailUrl,
  isRetrying,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStatusColor = status => {
    switch (status) {
      case "READY":
        return "green";
      case "ERROR":
        return "red";
      default:
        return "yellow";
    }
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      shadow="sm"
      p="md"
      withBorder
      onClick={() => !isUploading && onVideoClick(video)}
      sx={{
        cursor: isUploading ? "default" : "pointer",
        "&:hover": {
          backgroundColor: isUploading
            ? undefined
            : "var(--mantine-color-gray-0)",
        },
      }}
    >
      <Flex align="center" gap="md">
        <ActionIcon
          {...attributes}
          {...listeners}
          variant="subtle"
          color="gray"
          style={{
            cursor: isUploading ? "default" : "grab",
            touchAction: "none",
          }}
          title="Drag to reorder"
        >
          <IconGripVertical size={16} />
        </ActionIcon>

        <Box style={{ position: "relative", minWidth: 80, height: 60 }}>
          {video.video_thumbnail ? (
            <Image
              src={getVideoThumbnailUrl(video.video_thumbnail)}
              alt={video.title || "Video thumbnail"}
              width={80}
              height={60}
              radius="sm"
              fit="cover"
            />
          ) : (
            <Box
              style={{
                width: 80,
                height: 60,
                backgroundColor: "var(--mantine-color-gray-2)",
                borderRadius: "var(--mantine-radius-sm)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text size="lg">▶</Text>
            </Box>
          )}
        </Box>

        <Box style={{ flex: 1 }}>
          <Flex align="center" gap="xs" mb={4}>
            <Text fw={500} size="sm">
              {video.video_title || "Untitled Video"}
            </Text>
            {video.status && (
              <Badge size="xs" color={getStatusColor(video.status)}>
                {video.status}
              </Badge>
            )}
            {video.require_pitch && (
              <Badge size="xs" color="orange">
                Pitch Required
              </Badge>
            )}
          </Flex>
          <Text size="xs" c="dimmed" lineClamp={2}>
            {video.video_desc || "No description"}
          </Text>
          {isUploading && (
            <Box mt="xs">
              <Progress value={uploadProgress} size="sm" />
              <Text size="xs" c="dimmed" mt={4}>
                Uploading video... {uploadProgress}%
              </Text>
            </Box>
          )}
        </Box>

        <Group gap="xs">
          {video.status === "PENDING" && (
            <ActionIcon
              color="orange"
              variant="subtle"
              onClick={e => onRetryClick(e, video)}
              title="Retry video processing"
              disabled={isRetrying}
              loading={isRetrying}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          )}
          <ActionIcon
            color="red"
            variant="subtle"
            onClick={e => onDeleteClick(e, video)}
            title="Delete video"
            disabled={isUploading}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Flex>
    </Paper>
  );
}

function CourseFormPage() {
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [courseData, setCourseData] = useState({
    id: "",
    title: "",
    description: "",
    thumbnail: null,
  });

  const [videos, setVideos] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSavingCourse, setIsSavingCourse] = useState(false);
  const [thumbnailMessage, setThumbnailMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoModalMode, setVideoModalMode] = useState("add");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteVideoModalOpen, setIsDeleteVideoModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [uploadingVideos, setUploadingVideos] = useState({});
  const [retryingVideos, setRetryingVideos] = useState({});
  const [pendingVideoIds, setPendingVideoIds] = useState([]);
  const [pollCount, setPollCount] = useState(0);

  // Groups state
  const [allGroups, setAllGroups] = useState([]);
  const [courseGroups, setCourseGroups] = useState([]); // Just IDs
  const [groupSequentialStates, setGroupSequentialStates] = useState({}); // { groupId: isSequential }
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupLoadingStates, setGroupLoadingStates] = useState({});
  const [groupSearch, setGroupSearch] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchCourseData();
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Start polling if there are pending videos and haven't exceeded max attempts
    if (
      pendingVideoIds.length > 0 &&
      isEditMode &&
      pollCount < 30 &&
      pollCount > 0
    ) {
      const pollTimeout = setTimeout(() => {
        checkPendingVideos();
      }, 2000);

      return () => clearTimeout(pollTimeout);
    } else if (pollCount >= 30) {
      // Clear pending videos after 30 attempts (1 minute total with 2-second intervals)
      setPendingVideoIds([]);
      setPollCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVideoIds, isEditMode, pollCount]);

  const fetchCourseDetails = async jwt => {
    const response = await fetch(getApiEndpoint(`/v1/training/course/${id}`), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch course data");
    }

    const result = await response.json();
    return result?.data || null;
  };

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const course = await fetchCourseDetails(jwt);

      if (course) {
        setCourseData({
          id: course.id,
          title: course.course_title,
          description: course.course_desc,
          thumbnail: course.course_thumbnail,
        });

        const courseVideos = course.videos || [];
        setVideos(courseVideos);

        // Track pending videos
        const pending = courseVideos
          .filter(video => video.status === "PENDING")
          .map(video => video.id);

        setPendingVideoIds(pending);
        if (pending.length > 0) {
          setPollCount(1);
        }
      } else {
        setErrors({ general: "Course not found" });
      }
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchVideosList = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const course = await fetchCourseDetails(jwt);

      if (course && course.videos) {
        setVideos(course.videos);
      }
    } catch (err) {}
  };

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const jwt = await getVerifiedJWT();

      // Single request: get all groups annotated with whether this course is assigned
      const response = await fetch(
        getApiEndpoint(`/v1/groups/groups-for-course/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch groups");
      }

      const data = await response.json();
      const groups = data.data || [];

      setAllGroups(groups);

      // Derive the course group IDs and sequential states from the annotated response
      const groupsWithCourse = groups.filter(g => g.has_course).map(g => g.id);

      const sequentialStates = {};
      groups.forEach(g => {
        if (g.has_course) {
          sequentialStates[g.id] = g.is_sequential !== false;
        }
      });

      setCourseGroups(groupsWithCourse);
      setGroupSequentialStates(sequentialStates);
    } catch (err) {
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleToggleGroup = async (groupId, isCurrentlyInGroup) => {
    const buttonKey = `${groupId}-${!isCurrentlyInGroup}`;
    setGroupLoadingStates(prev => ({ ...prev, [buttonKey]: true }));

    try {
      const jwt = await getVerifiedJWT();
      const payload = {
        group_id: groupId,
        course_id: parseInt(id),
        include: !isCurrentlyInGroup,
        is_sequential: true, // Default to sequential when adding
      };

      const response = await fetch(getApiEndpoint("/v1/groups/group/course"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to update group");
      }

      // Update local state
      if (isCurrentlyInGroup) {
        setCourseGroups(prev => prev.filter(gId => gId !== groupId));
        setGroupSequentialStates(prev => {
          const newStates = { ...prev };
          delete newStates[groupId];
          return newStates;
        });
      } else {
        setCourseGroups(prev => [...prev, groupId]);
        setGroupSequentialStates(prev => ({
          ...prev,
          [groupId]: true, // Default to true when adding
        }));
      }
    } catch (error) {
      setErrors({
        ...errors,
        groups: "Failed to update group. Please try again.",
      });
    } finally {
      setGroupLoadingStates(prev => ({ ...prev, [buttonKey]: false }));
    }
  };

  const checkPendingVideos = async () => {
    try {
      // Increment poll count
      setPollCount(prev => prev + 1);

      const jwt = await getVerifiedJWT();
      const course = await fetchCourseDetails(jwt);

      if (course && course.videos) {
        const updatedVideos = course.videos;

        // Update videos state
        setVideos(updatedVideos);

        // Check which videos are still pending
        const stillPending = updatedVideos
          .filter(
            video =>
              pendingVideoIds.includes(video.id) && video.status === "PENDING"
          )
          .map(video => video.id);

        // Update pending IDs (this will trigger another poll if still pending)
        setPendingVideoIds(stillPending);

        // Reset poll count if all videos are no longer pending
        if (stillPending.length === 0) {
          setPollCount(0);
        }
      }
    } catch (err) {
      // Silently fail and try again on next poll
    }
  };

  const handleDrop = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("image/")) {
        setCourseData(prev => ({
          ...prev,
          thumbnail: file,
        }));

        if (isEditMode && courseData.id) {
          await uploadThumbnail(file);
        }
      }
    }
  };

  const uploadThumbnail = async file => {
    if (!courseData.id) return;

    setThumbnailMessage("Saving...");

    try {
      const jwt = await getVerifiedJWT();
      const formData = new FormData();
      formData.append("course_id", courseData.id);
      formData.append("course_thumbnail", file);

      const response = await fetch(
        getApiEndpoint("/v1/training/course/thumbnail"),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        setThumbnailMessage("✓ Thumbnail updated successfully");
        setTimeout(() => setThumbnailMessage(""), 3000);
      } else {
        setThumbnailMessage("✗ Failed to update thumbnail");
        setTimeout(() => setThumbnailMessage(""), 3000);
      }
    } catch (error) {
      setThumbnailMessage("✗ Network error occurred");
      setTimeout(() => setThumbnailMessage(""), 3000);
    }
  };

  const validateCourseForm = () => {
    const newErrors = {};

    if (!courseData.title.trim()) {
      newErrors.title = "Course title is required";
    }

    if (!isEditMode && !courseData.thumbnail) {
      newErrors.thumbnail = "Course thumbnail is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCourseSave = async () => {
    if (!validateCourseForm()) return;

    setIsSavingCourse(true);
    try {
      const jwt = await getVerifiedJWT();

      if (isEditMode) {
        const payload = {
          course_id: parseInt(courseData.id),
          course_title: courseData.title,
          course_desc: courseData.description,
        };

        const response = await fetch(getApiEndpoint("/v1/training/course"), {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          setErrors({});
          // Navigate back to admin page
          navigate("/admin");
        } else {
          setErrors({ general: "Failed to update course. Please try again." });
        }
      } else {
        const formData = new FormData();
        formData.append("course_title", courseData.title);
        formData.append("course_desc", courseData.description);
        formData.append("course_thumbnail", courseData.thumbnail);

        const response = await fetch(getApiEndpoint("/v1/training/course"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
          body: formData,
        });

        if (response.ok) {
          // Navigate back to admin page
          navigate("/admin");
        } else {
          setErrors({ general: "Failed to create course. Please try again." });
        }
      }
    } catch (error) {
      setErrors({
        general: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSavingCourse(false);
    }
  };

  const handleAddVideoClick = async () => {
    // Validate course form first
    if (!validateCourseForm()) {
      return; // Validation errors will be displayed
    }

    // Open video modal immediately after validation passes
    setVideoModalMode("add");
    setSelectedVideo(null);
    setVideoModalOpen(true);

    // Save the course in the background (fire and forget)
    const saveCourseInBackground = async () => {
      setIsSavingCourse(true);
      try {
        const jwt = await getVerifiedJWT();

        if (isEditMode) {
          const payload = {
            course_id: parseInt(courseData.id),
            course_title: courseData.title,
            course_desc: courseData.description,
          };

          const response = await fetch(getApiEndpoint("/v1/training/course"), {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${jwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            setErrors({});
          } else {
            setErrors({
              general: "Failed to update course. Please try again.",
            });
          }
        } else {
          const formData = new FormData();
          formData.append("course_title", courseData.title);
          formData.append("course_desc", courseData.description);
          formData.append("course_thumbnail", courseData.thumbnail);

          const response = await fetch(getApiEndpoint("/v1/training/course"), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
            body: formData,
          });

          if (response.ok) {
            const result = await response.json();
            // Update the course ID in state so subsequent operations work
            setCourseData(prev => ({ ...prev, id: result.data.id }));
            setErrors({});
            // Navigate to edit mode in the background
            navigate(`/training-courses/edit/${result.data.id}`, {
              replace: true,
            });
          } else {
            setErrors({
              general: "Failed to create course. Please try again.",
            });
          }
        }
      } catch (error) {
        setErrors({
          general: "Network error. Please check your connection and try again.",
        });
      } finally {
        setIsSavingCourse(false);
      }
    };

    // Execute save in background
    saveCourseInBackground();
  };

  const handleVideoCardClick = video => {
    setVideoModalMode("edit");
    setSelectedVideo(video);
    setVideoModalOpen(true);
  };

  const handleDeleteVideoClick = (e, video) => {
    e.stopPropagation();
    setVideoToDelete(video);
    setIsDeleteVideoModalOpen(true);
  };

  const handleCloseDeleteVideoModal = () => {
    setIsDeleteVideoModalOpen(false);
    setVideoToDelete(null);
  };

  const handleVideoModalClose = () => {
    setVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const handleDeleteCourse = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/training/course/${courseData.id}`),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        navigate("/admin");
      } else {
        setErrors({ general: "Failed to delete course. Please try again." });
      }
    } catch (error) {
      setErrors({
        general: "Network error. Please check your connection and try again.",
      });
    }
    handleCloseDeleteModal();
  };

  const uploadFileWithAxios = async (uploadUrl, file, onProgress) => {
    return await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type },
      onUploadProgress: progressEvent => {
        const percent = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );
        onProgress(percent);
      },
    });
  };

  const handleVideoSuccess = async data => {
    if (videoModalMode === "add") {
      const videoId = data.video.id;

      // Start tracking this upload
      setUploadingVideos(prev => ({ ...prev, [videoId]: 0 }));

      // Fetch the video list immediately to show the new video in correct sort order
      await fetchVideosList();

      // Start upload without blocking (fire and forget)
      uploadFileWithAxios(data.uploadUrl, data.videoFile, percent => {
        setUploadingVideos(prev => ({ ...prev, [videoId]: percent }));
      })
        .then(async () => {
          // Upload successful - fetch updated videos list to get any status updates
          await fetchVideosList();

          // Check for pending videos and start polling if needed
          const jwt = await getVerifiedJWT();
          if (jwt) {
            const course = await fetchCourseDetails(jwt);

            if (course && course.videos) {
              setVideos(course.videos);

              // Check for pending videos
              const pending = course.videos
                .filter(video => video.status === "PENDING")
                .map(video => video.id);

              if (pending.length > 0) {
                setPendingVideoIds(pending);
                setPollCount(1); // Start polling
              }
            }
          }
        })
        .catch(error => {
          setErrors({
            general: "Video upload failed. Please try again.",
          });
        })
        .finally(() => {
          // Remove from uploading videos
          setUploadingVideos(prev => {
            const newState = { ...prev };
            delete newState[videoId];
            return newState;
          });
        });
    } else {
      if (Array.isArray(data)) {
        setVideos(data);
      } else {
        setVideos(prevVideos =>
          prevVideos.map(video =>
            video.id === selectedVideo.id ? { ...video, ...data } : video
          )
        );
      }
    }
  };

  const handleConfirmDeleteVideo = async videoId => {
    try {
      const jwt = await getVerifiedJWT();

      const response = await fetch(
        getApiEndpoint(`/v1/training/video/${videoId}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const responseData = await response.json();

        if (responseData && Array.isArray(responseData)) {
          setVideos(responseData);
        } else {
          setVideos(prevVideos =>
            prevVideos.filter(video => video.id !== videoId)
          );
        }
      } else {
        setErrors({
          general: "Failed to delete video. Please try again.",
        });
      }
    } catch (error) {
      setErrors({
        general: "Network error. Please check your connection and try again.",
      });
    }
  };

  const getThumbnailUrl = thumbnail => {
    if (!thumbnail) return null;
    if (thumbnail instanceof File) {
      return URL.createObjectURL(thumbnail);
    }
    return thumbnail;
  };

  const getVideoThumbnailUrl = thumbnail => {
    if (!thumbnail) return null;
    if (thumbnail instanceof File) {
      return URL.createObjectURL(thumbnail);
    }
    return thumbnail;
  };

  const handleRetryVideo = async (e, video) => {
    e.stopPropagation();

    if (retryingVideos[video.id]) {
      return; // Already retrying
    }

    // Mark this video as retrying
    setRetryingVideos(prev => ({ ...prev, [video.id]: true }));

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/training/video/rekick/${video.id}`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        // Add video to pending list and start polling
        if (!pendingVideoIds.includes(video.id)) {
          setPendingVideoIds(prev => [...prev, video.id]);
        }
        if (pollCount === 0) {
          setPollCount(1);
        }

        // Fetch updated video list
        await fetchVideosList();
      } else {
        showError("Failed to retry video processing. Please try again.");
      }
    } catch (error) {
      handleGenericError(
        error,
        "Network error while retrying video. Please try again."
      );
    } finally {
      // Remove from retrying videos after a short delay
      setTimeout(() => {
        setRetryingVideos(prev => {
          const newState = { ...prev };
          delete newState[video.id];
          return newState;
        });
      }, 1000);
    }
  };

  const handleDragEnd = async event => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = videos.findIndex(video => video.id === active.id);
    const newIndex = videos.findIndex(video => video.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update the UI
    const reorderedVideos = arrayMove(videos, oldIndex, newIndex);
    setVideos(reorderedVideos);

    try {
      const jwt = await getVerifiedJWT();

      const payload = {
        id: active.id,
        sort_order: newIndex + 1,
      };

      const response = await fetch(
        getApiEndpoint("/v1/training/video/update"),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        // Revert on failure
        setVideos(videos);
        showError("Failed to reorder videos. Please try again.");
      }
    } catch (error) {
      // Revert on error
      setVideos(videos);
      handleGenericError(error, "Failed to reorder videos. Please try again.");
    }
  };

  const adminHeader = (
    <div className="org-page-custom-header">
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Admin</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {ADMIN_SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = section.id === "course-creation";
            return (
              <button
                key={section.id}
                role="tab"
                aria-selected={isActive}
                className={`org-page-nav-tab${isActive ? " active" : ""}`}
                onClick={() => section.path && navigate(section.path)}
              >
                <Icon size={16} />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  return (
    <PageContent customHeader={adminHeader}>
      {loading && (
        <Paper p="xl" withBorder>
          <Flex align="center" gap="sm">
            <Loader size="sm" />
            <Text>Loading course data...</Text>
          </Flex>
        </Paper>
      )}

      {errors.general && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error"
          color="red"
          variant="light"
        >
          {errors.general}
        </Alert>
      )}

      <div className="program-details-container" style={{ paddingTop: 0 }}>
        <div className="program-details-layout">
          {/* Left Sidebar */}
          <div className="program-sidebar">
            {/* Course Details Card */}
            <div className="progress-card">
              <TextInput
                value={courseData.title}
                onChange={e =>
                  setCourseData(prev => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="Course name..."
                error={errors.title}
                mb="sm"
                styles={{
                  input: {
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#1a1a1a",
                    border: "none",
                    borderBottom: "2px solid #e5e7eb",
                    borderRadius: 0,
                    padding: "4px 0",
                    background: "transparent",
                    height: "auto",
                  },
                }}
              />

              {/* Cover Image — 16:9, full sidebar width */}
              <FileButton
                onChange={file => {
                  if (file) {
                    setCourseData(prev => ({ ...prev, thumbnail: file }));
                    if (isEditMode && courseData.id) {
                      uploadThumbnail(file);
                    }
                  }
                }}
                accept="image/*"
              >
                {props => (
                  <div
                    {...props}
                    style={{
                      width: "100%",
                      paddingTop: "56.25%",
                      position: "relative",
                      borderRadius: 10,
                      border: `2px dashed ${errors.thumbnail ? "#ef4444" : "#e5e7eb"}`,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: courseData.thumbnail
                        ? "transparent"
                        : "#f9fafb",
                      marginBottom: 8,
                    }}
                    onDrop={handleDrop}
                  >
                    {courseData.thumbnail ? (
                      <>
                        <img
                          src={getThumbnailUrl(courseData.thumbnail)}
                          alt="Cover"
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,0.45)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                          onMouseEnter={e =>
                            (e.currentTarget.style.opacity = 1)
                          }
                          onMouseLeave={e =>
                            (e.currentTarget.style.opacity = 0)
                          }
                        >
                          <div style={{ textAlign: "center" }}>
                            <IconUpload size={18} color="white" />
                            <Text c="white" fw={600} size="xs" mt={4}>
                              Change Image
                            </Text>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                        }}
                      >
                        <IconUpload size={22} stroke={1.5} color="#9ca3af" />
                        <Text c="dimmed" size="xs" fw={500}>
                          Add cover image
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </FileButton>
              {errors.thumbnail && (
                <Text c="red" size="sm" mb="sm">
                  {errors.thumbnail}
                </Text>
              )}
              {thumbnailMessage && (
                <Text
                  size="xs"
                  mb="sm"
                  c={
                    thumbnailMessage.includes("✓")
                      ? "green"
                      : thumbnailMessage.includes("✗")
                        ? "red"
                        : "dimmed"
                  }
                >
                  {thumbnailMessage}
                </Text>
              )}

              <Textarea
                label="DESCRIPTION"
                placeholder="Enter course description"
                value={courseData.description}
                onChange={e =>
                  setCourseData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={4}
                size="sm"
              />
            </div>

            {/* Groups Card */}
            {isEditMode && (
              <div
                className="progress-card"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  overflow: "hidden",
                }}
              >
                <div>
                  <Text fw={700} size="sm" style={{ color: "#1a1a1a" }}>
                    Groups
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    Manage which groups have access to this course
                  </Text>
                </div>

                <TextInput
                  placeholder="Search groups..."
                  value={groupSearch}
                  onChange={e => setGroupSearch(e.target.value)}
                  size="xs"
                  leftSection={<IconSearch size={12} />}
                  styles={{
                    input: {
                      borderRadius: 8,
                      fontSize: "0.8125rem",
                    },
                  }}
                />

                {errors.groups && (
                  <Alert
                    icon={<IconAlertTriangle size={14} />}
                    color="red"
                    variant="light"
                    p="xs"
                  >
                    <Text size="xs">{errors.groups}</Text>
                  </Alert>
                )}

                {loadingGroups ? (
                  <Flex align="center" gap="sm">
                    <Loader size="sm" />
                    <Text size="sm">Loading groups...</Text>
                  </Flex>
                ) : allGroups.length > 0 ? (
                  <Stack gap="md" style={{ flex: 1, overflowY: "auto" }}>
                    {/* Added groups */}
                    <Box>
                      <Text
                        size="xs"
                        fw={600}
                        c="green.7"
                        mb={6}
                        tt="uppercase"
                        style={{ letterSpacing: "0.04em" }}
                      >
                        Added (
                        {
                          allGroups.filter(g => courseGroups.includes(g.id))
                            .length
                        }
                        )
                      </Text>
                      {allGroups
                        .filter(g => courseGroups.includes(g.id))
                        .filter(g =>
                          groupSearch
                            ? g.name
                                .toLowerCase()
                                .includes(groupSearch.toLowerCase()) ||
                              (g.manager_name || "")
                                .toLowerCase()
                                .includes(groupSearch.toLowerCase())
                            : true
                        ).length === 0 ? (
                        <Paper
                          p="xs"
                          withBorder
                          style={{
                            borderStyle: "dashed",
                            textAlign: "center",
                          }}
                        >
                          <Text size="xs" c="dimmed">
                            {groupSearch ? "No matches" : "No groups added yet"}
                          </Text>
                        </Paper>
                      ) : (
                        <Stack gap="xs">
                          {allGroups
                            .filter(g => courseGroups.includes(g.id))
                            .filter(g =>
                              groupSearch
                                ? g.name
                                    .toLowerCase()
                                    .includes(groupSearch.toLowerCase()) ||
                                  (g.manager_name || "")
                                    .toLowerCase()
                                    .includes(groupSearch.toLowerCase())
                                : true
                            )
                            .map(group => {
                              const buttonKey = `${group.id}-false`;
                              const isLoading = groupLoadingStates[buttonKey];
                              const isSequential =
                                groupSequentialStates[group.id] !== false;
                              return (
                                <Paper
                                  key={group.id}
                                  p="sm"
                                  withBorder
                                  style={{
                                    backgroundColor:
                                      "var(--mantine-color-green-0)",
                                  }}
                                >
                                  <Flex
                                    align="flex-start"
                                    justify="space-between"
                                    gap="sm"
                                  >
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                      <Text fw={500} size="xs" truncate>
                                        {group.name}
                                      </Text>
                                      {group.manager_name && (
                                        <Text
                                          size="xs"
                                          c="dimmed"
                                          mb={6}
                                          truncate
                                        >
                                          {group.manager_name}
                                        </Text>
                                      )}
                                      <Switch
                                        label="Sequential"
                                        size="xs"
                                        checked={isSequential}
                                        onChange={async e => {
                                          const newValue =
                                            e.currentTarget.checked;
                                          setGroupSequentialStates(prev => ({
                                            ...prev,
                                            [group.id]: newValue,
                                          }));
                                          try {
                                            const jwt = await getVerifiedJWT();
                                            const response = await fetch(
                                              getApiEndpoint(
                                                "/v1/groups/group/course"
                                              ),
                                              {
                                                method: "PUT",
                                                headers: {
                                                  "Content-Type":
                                                    "application/json",
                                                  Authorization: `Bearer ${jwt}`,
                                                },
                                                body: JSON.stringify({
                                                  group_id: group.id,
                                                  course_id: parseInt(id),
                                                  include: true,
                                                  is_sequential: newValue,
                                                }),
                                              }
                                            );
                                            if (!response.ok) {
                                              setGroupSequentialStates(
                                                prev => ({
                                                  ...prev,
                                                  [group.id]: !newValue,
                                                })
                                              );
                                              setErrors({
                                                ...errors,
                                                groups:
                                                  "Failed to update sequential mode.",
                                              });
                                            }
                                          } catch (err) {
                                            setGroupSequentialStates(prev => ({
                                              ...prev,
                                              [group.id]: !newValue,
                                            }));
                                            setErrors({
                                              ...errors,
                                              groups:
                                                "Failed to update sequential mode.",
                                            });
                                          }
                                        }}
                                        disabled={Object.values(
                                          groupLoadingStates
                                        ).some(l => l)}
                                      />
                                    </Box>
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      color="red"
                                      onClick={() =>
                                        handleToggleGroup(group.id, true)
                                      }
                                      loading={isLoading}
                                      disabled={Object.values(
                                        groupLoadingStates
                                      ).some(l => l)}
                                      style={{ flexShrink: 0 }}
                                    >
                                      Remove
                                    </Button>
                                  </Flex>
                                </Paper>
                              );
                            })}
                        </Stack>
                      )}
                    </Box>

                    {/* Not added groups */}
                    <Box>
                      <Text
                        size="xs"
                        fw={600}
                        c="dimmed"
                        mb={6}
                        tt="uppercase"
                        style={{ letterSpacing: "0.04em" }}
                      >
                        Not Added (
                        {
                          allGroups.filter(g => !courseGroups.includes(g.id))
                            .length
                        }
                        )
                      </Text>
                      {allGroups
                        .filter(g => !courseGroups.includes(g.id))
                        .filter(g =>
                          groupSearch
                            ? g.name
                                .toLowerCase()
                                .includes(groupSearch.toLowerCase()) ||
                              (g.manager_name || "")
                                .toLowerCase()
                                .includes(groupSearch.toLowerCase())
                            : true
                        ).length === 0 ? (
                        <Paper
                          p="xs"
                          withBorder
                          style={{
                            borderStyle: "dashed",
                            textAlign: "center",
                          }}
                        >
                          <Text size="xs" c="dimmed">
                            {groupSearch ? "No matches" : "All groups added"}
                          </Text>
                        </Paper>
                      ) : (
                        <Stack gap="xs">
                          {allGroups
                            .filter(g => !courseGroups.includes(g.id))
                            .filter(g =>
                              groupSearch
                                ? g.name
                                    .toLowerCase()
                                    .includes(groupSearch.toLowerCase()) ||
                                  (g.manager_name || "")
                                    .toLowerCase()
                                    .includes(groupSearch.toLowerCase())
                                : true
                            )
                            .map(group => {
                              const buttonKey = `${group.id}-true`;
                              const isLoading = groupLoadingStates[buttonKey];
                              return (
                                <Paper key={group.id} p="sm" withBorder>
                                  <Flex
                                    align="center"
                                    justify="space-between"
                                    gap="sm"
                                  >
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                      <Text fw={500} size="xs" truncate>
                                        {group.name}
                                      </Text>
                                      {group.manager_name && (
                                        <Text size="xs" c="dimmed" truncate>
                                          {group.manager_name}
                                        </Text>
                                      )}
                                    </Box>
                                    <Button
                                      size="xs"
                                      variant="filled"
                                      color="green"
                                      onClick={() =>
                                        handleToggleGroup(group.id, false)
                                      }
                                      loading={isLoading}
                                      disabled={Object.values(
                                        groupLoadingStates
                                      ).some(l => l)}
                                      style={{ flexShrink: 0 }}
                                    >
                                      Add
                                    </Button>
                                  </Flex>
                                </Paper>
                              );
                            })}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                ) : (
                  <Paper
                    p="md"
                    withBorder
                    style={{ borderStyle: "dashed", textAlign: "center" }}
                  >
                    <Text size="xs" c="dimmed">
                      No groups available
                    </Text>
                  </Paper>
                )}
              </div>
            )}
          </div>

          {/* Right Content Area */}
          <div className="program-content">
            <div
              className="tasks-list"
              style={{
                flex: 1,
                overflowY: "scroll",
                overflowX: "hidden",
                minHeight: 0,
                paddingRight: 4,
                maxHeight: "100%",
              }}
            >
              <Group justify="space-between" align="center" mb="md">
                <Title size="h2">Videos</Title>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={handleAddVideoClick}
                  size="sm"
                >
                  Add Video
                </Button>
              </Group>

              {videos.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={videos.map(v => v.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Stack gap="sm">
                      {videos.map(video => {
                        const isUploading = uploadingVideos.hasOwnProperty(
                          video.id
                        );
                        const uploadProgress = uploadingVideos[video.id] || 0;

                        return (
                          <SortableVideoItem
                            key={video.id}
                            video={video}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            onVideoClick={handleVideoCardClick}
                            onDeleteClick={handleDeleteVideoClick}
                            onRetryClick={handleRetryVideo}
                            getVideoThumbnailUrl={getVideoThumbnailUrl}
                            isRetrying={retryingVideos[video.id] || false}
                          />
                        );
                      })}
                    </Stack>
                  </SortableContext>
                </DndContext>
              ) : (
                <Paper
                  p="xl"
                  withBorder
                  style={{ borderStyle: "dashed" }}
                  className="empty-state"
                >
                  <Text ta="center" c="dimmed">
                    No videos added yet. Click "Add Video" to get started.
                  </Text>
                </Paper>
              )}
            </div>

            {/* Sticky bottom actions */}
            <div
              style={{
                padding: "16px 24px 24px",
                borderTop: "1px solid #f3f4f6",
                flexShrink: 0,
              }}
            >
              <Stack gap="sm">
                <Button
                  fullWidth
                  onClick={handleCourseSave}
                  loading={isSavingCourse}
                  style={{
                    background: "#ff6b35",
                    border: "none",
                    height: 48,
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  {isEditMode ? "Save Course" : "Create Course"}
                </Button>
                {isEditMode && (
                  <Button
                    fullWidth
                    color="red"
                    variant="outline"
                    onClick={handleDeleteCourse}
                  >
                    Delete Course
                  </Button>
                )}
              </Stack>
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={videoModalOpen}
        onClose={handleVideoModalClose}
        onSuccess={handleVideoSuccess}
        mode={videoModalMode}
        initialData={selectedVideo}
        videosLength={videos.length}
        courseId={courseData.id}
        getVerifiedJWT={getVerifiedJWT}
      />

      <DeleteCourseModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        course={courseData}
        onConfirmDelete={handleConfirmDelete}
      />

      <DeleteVideoModal
        isOpen={isDeleteVideoModalOpen}
        onClose={handleCloseDeleteVideoModal}
        video={videoToDelete}
        onConfirmDelete={handleConfirmDeleteVideo}
      />
    </PageContent>
  );
}

export default CourseFormPage;
